// ProgramaModulosSection.js — Listado del programa (módulo + tema + docente) en
// columnas, sobre un fondo de imagen, con un botón para entrar al curso.
//
// Comparte las reglas de CursosPorPerfilSection: los contenidos se agrupan por
// perfil (subrol), el usuario ve solo el suyo y el admin los ve todos, y el
// orden dentro de cada perfil sale de config.orden_por_perfil.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { BACKEND_BASE } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { buildBg } from '../bgHelpers';
import { useBloquesPorPerfil } from '../perfilesDocs';

const Wrapper = styled.section`
  position: relative;
  overflow: hidden;
  padding: ${p => p.$padding || '3.5rem 2rem 3rem'};
  background: ${p => p.$bg || '#1b75bc'};
  ${p => p.$bgImage && `
    background-image: ${p.$overlay ? `linear-gradient(${p.$overlay}, ${p.$overlay}), ` : ''}url(${p.$bgImage});
    background-size: ${p.$bgSize || 'cover'};
    background-position: ${p.$bgPos || 'center'};
    background-repeat: no-repeat;
  `}
  ${p => p.$radio ? `border-radius: ${p.$radio};` : ''}
  @media (max-width: 768px) {
    padding: ${p => p.$paddingMovil || '2.5rem 1.25rem'};
    ${p => p.$radio ? 'border-radius: 0;' : ''}
  }
`;

const Inner = styled.div`
  max-width: ${p => p.$maxWidth || '1100px'};
  margin: 0 auto;
`;

const Titulo = styled.h2`
  margin: ${p => p.$margen || '0 0 1.8rem'};
  font-family: inherit;
  font-weight: ${p => p.$peso || '700'};
  font-size: ${p => p.$size || '22px'};
  line-height: 1.25;
  letter-spacing: 0.2px;
  color: ${p => p.$color || '#ffffff'};
  text-align: ${p => p.$align || 'left'};
  text-transform: ${p => p.$transform || 'none'};
`;

const Columnas = styled.div`
  display: grid;
  grid-template-columns: repeat(${p => p.$cols || 2}, 1fr);
  gap: ${p => p.$gap || '2.5rem'};
  @media (max-width: 768px) { grid-template-columns: 1fr; gap: 1.5rem; }
`;

const Columna = styled.div`
  padding-left: ${p => p.$conLinea ? (p.$sep || '2.5rem') : '0'};
  ${p => p.$conLinea ? `border-left: ${p.$grosor || '1px'} ${p.$estilo || 'solid'} ${p.$color || 'rgba(255,255,255,0.6)'};` : ''}
  @media (max-width: 768px) { padding-left: 0; border-left: none; }
`;

const Item = styled.div`
  & + & { margin-top: ${p => p.$sep || '1.35rem'}; }
`;

const Etiqueta = styled.div`
  font-weight: ${p => p.$peso || '700'};
  font-size: ${p => p.$size || '17px'};
  color: ${p => p.$color || '#ffffff'};
  line-height: 1.3;
`;

const Tema = styled.div`
  font-weight: ${p => p.$peso || '400'};
  font-size: ${p => p.$size || '17px'};
  color: ${p => p.$color || '#ffffff'};
  line-height: 1.35;
  strong { font-weight: 700; }
`;

const Docente = styled.div`
  font-weight: ${p => p.$peso || '700'};
  font-size: ${p => p.$size || '17px'};
  color: ${p => p.$color || '#ffffff'};
  line-height: 1.35;
`;

const FilaBoton = styled.div`
  display: flex;
  justify-content: ${p => p.$align || 'center'};
  margin: ${p => p.$margen || '2.5rem 0 0'};
`;

const Boton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  cursor: pointer;
  font-family: inherit;
  font-weight: ${p => p.$peso || '600'};
  font-size: ${p => p.$size || '15px'};
  letter-spacing: 0.4px;
  padding: ${p => p.$padding || '12px 28px'};
  border-radius: ${p => p.$radio || '4px'};
  color: ${p => p.$color || '#ffffff'};
  background: ${p => p.$bg || 'transparent'};
  border: ${p => p.$borde || '1px solid #ffffff'};
  transition: 0.2s;
  &:hover { background: ${p => p.$hoverBg || 'rgba(255,255,255,0.15)'}; }
`;

const Vacio = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: ${p => p.$color || 'rgba(255,255,255,0.85)'};
`;

// Avión de papel del diseño (SVG inline para no depender de una librería).
const IconoAvion = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M2.4 11.2 21 3.2c.7-.3 1.4.4 1.1 1.1l-8 18.6c-.3.7-1.3.7-1.5-.1l-2-6.6-6.6-2c-.8-.2-.8-1.2-.1-1.5z" />
  </svg>
);

const resolveImg = (img) => (!img ? null : (img.startsWith('http') ? img : `${BACKEND_BASE}/${img}`));

// Reparte los ítems en N columnas llenando la primera antes que la segunda
// (Módulos 1-6 a la izquierda, 7-12 a la derecha), como en el diseño.
const repartirEnColumnas = (items, cols) => {
  const n = Math.max(1, cols);
  const porColumna = Math.ceil(items.length / n);
  const grupos = [];
  for (let i = 0; i < items.length; i += porColumna) grupos.push(items.slice(i, i + porColumna));
  while (grupos.length < n) grupos.push([]);
  return grupos;
};

const ProgramaModulosSection = ({ config = {}, proyecto, openLogin, tl }) => {
  const c = config;
  const navigate = useNavigate();
  const { user } = useAuth();
  const traducir = (v, fallback = '') => (tl ? (tl(v) || fallback) : (v || fallback));

  const { bloques: bloquesPerfil, verTodos, sinPerfil, cargando } = useBloquesPorPerfil(c, proyecto);

  if (cargando) return null;
  if (sinPerfil && c.ocultar_sin_perfil === true) return null;

  const bloques = bloquesPerfil.map(b => ({
    ...b,
    titulo: traducir((c.titulos_grupo || {})[b.key], '') || traducir(c.titulo, ''),
  }));

  const mostrarVacios = verTodos || c.mostrar_grupo_vacio === true;
  const bloquesVisibles = mostrarVacios ? bloques : bloques.filter(b => b.docs.length > 0);
  if (bloquesVisibles.length === 0 && c.ocultar_si_vacio === true) return null;

  // `traducir`: el texto puede venir traducido ({es, pt}); vacío a propósito = sin etiqueta.
  const etiqueta = c.etiqueta_modulo != null ? traducir(c.etiqueta_modulo) : 'Módulo';
  const titulosDoc = c.titulos_doc || {};
  const modos = proyecto?.config_json?.modos_habilitados || {};

  const tituloDe = (d) => traducir(titulosDoc[String(d.id)], '') || traducir(d.titulo);
  const docenteDe = (d) => {
    const sel = Array.isArray(d.docentes_sel) ? d.docentes_sel : [];
    if (sel.length > 0) return sel[0].nombre;
    const mod = (d.modulos || []).find(m => m.docente_nombre);
    if (mod) return mod.docente_nombre;
    return d.docente || '';
  };

  const destinoDoc = (d) => {
    const modo = c.destino_boton || 'auto';
    if (modo === 'consulta') return `/consulta/${d.id}`;
    if (modo === 'evaluacion') return `/evaluacion/${d.id}`;
    if (modo === 'mentor') return `/mentor/${d.id}`;
    if (Number(d.modo_mentor) === 1 && modos.mentor !== false) return `/mentor/${d.id}`;
    if (Number(d.modo_evaluacion) === 1 && modos.evaluacion !== false) return `/evaluacion/${d.id}`;
    return `/consulta/${d.id}`;
  };

  // El botón lleva a la URL configurada (por defecto la página de contenidos) o,
  // si se deja en "primer módulo", al primer contenido del perfil.
  const irAlCurso = (docs) => {
    if (!user) { if (openLogin) openLogin(); else navigate('/login'); return; }
    const url = (c.url_boton || '').trim();
    if (url) {
      if (url.startsWith('http')) window.open(url, '_blank');
      else navigate(url);
      return;
    }
    if (docs.length > 0) navigate(destinoDoc(docs[0]));
  };

  const cols = parseInt(c.columnas, 10) || 2;
  const bgImage = resolveImg(c.imagen_fondo);

  return (
    <Wrapper
      $bg={buildBg(c, '#1b75bc')}
      $bgImage={bgImage}
      $overlay={c.overlay_fondo}
      $bgSize={c.tamano_fondo}
      $bgPos={c.posicion_fondo}
      $padding={c.padding}
      $paddingMovil={c.padding_movil}
      $radio={c.radio_esquinas}
    >
      <Inner $maxWidth={c.ancho_maximo}>
        {bloquesVisibles.length === 0 ? (
          <Vacio $color={c.color_texto}>
            {traducir(c.texto_vacio, '') || 'Todavía no hay contenidos disponibles para tu perfil.'}
          </Vacio>
        ) : bloquesVisibles.map((b, bi) => {
          const grupos = repartirEnColumnas(b.docs, cols);
          let n = 0;
          return (
            <div key={b.key || '_sin_perfil'} style={bi > 0 ? { marginTop: c.separacion_grupos || '3rem' } : undefined}>
              {b.titulo && (
                <Titulo
                  $size={c.tamano_titulo}
                  $color={c.color_titulo}
                  $peso={c.peso_titulo}
                  $align={c.alineacion_titulo}
                  $margen={c.margen_titulo}
                  $transform={c.transformar_titulo}
                  dangerouslySetInnerHTML={{ __html: b.titulo }}
                />
              )}
              <Columnas $cols={cols} $gap={c.gap}>
                {grupos.map((docs, gi) => (
                  <Columna
                    key={gi}
                    $conLinea={gi > 0 && c.mostrar_separador !== false}
                    $sep={c.padding_separador}
                    $color={c.color_separador}
                    $grosor={c.grosor_separador}
                    $estilo={c.estilo_separador}
                  >
                    {docs.map(d => {
                      n += 1;
                      const docente = docenteDe(d);
                      return (
                        <Item key={d.id} $sep={c.separacion_items}>
                          {c.mostrar_numero_modulo !== false && (
                            <Etiqueta $size={c.tamano_etiqueta} $color={c.color_etiqueta} $peso={c.peso_etiqueta}>
                              {`${etiqueta} ${n}`.trim()}
                            </Etiqueta>
                          )}
                          <Tema
                            $size={c.tamano_tema}
                            $color={c.color_tema}
                            $peso={c.peso_tema}
                            dangerouslySetInnerHTML={{ __html: tituloDe(d) }}
                          />
                          {c.mostrar_docente !== false && docente && (
                            <Docente $size={c.tamano_docente} $color={c.color_docente} $peso={c.peso_docente}>
                              {docente}
                            </Docente>
                          )}
                        </Item>
                      );
                    })}
                  </Columna>
                ))}
              </Columnas>
              {c.mostrar_boton !== false && (
                <FilaBoton $align={c.alineacion_boton || 'center'} $margen={c.margen_boton}>
                  <Boton
                    onClick={() => irAlCurso(b.docs)}
                    $bg={c.color_boton}
                    $color={c.color_texto_boton}
                    $borde={c.borde_boton}
                    $radio={c.radio_boton}
                    $size={c.tamano_boton}
                    $peso={c.peso_boton}
                    $padding={c.padding_boton}
                    $hoverBg={c.color_boton_hover}
                  >
                    {c.mostrar_icono_boton !== false && <IconoAvion size={parseInt(c.tamano_icono_boton, 10) || 15} />}
                    {traducir(c.texto_boton, '') || 'IR AL CURSO'}
                  </Boton>
                </FilaBoton>
              )}
            </div>
          );
        })}
      </Inner>
    </Wrapper>
  );
};

export default ProgramaModulosSection;
